// src/components/Chat.jsx
import React, { useEffect, useRef, useState } from "react";
import ChatComposer from "../components/ChatComposer"; 
import ProductCard from "../components/ProductCard";

// --- HELPER: Safe Text Formatter (Replaces ReactMarkdown) ---
// This manually handles **bold** and [links](url) to prevent library crashes
function FormattedText({ text }) {
  if (!text) return null;

  // 1. Split text by new lines to create paragraphs
  const lines = text.split('\n');

  const parseLinks = (str, keyPrefix) => {
    // Regex to find [Label](Url)
    const parts = str.split(/(\[.*?\]\(.*?\))/g);
    return parts.map((part, i) => {
      const match = part.match(/\[(.*?)\]\((.*?)\)/);
      if (match) {
        return (
          <a 
            key={`${keyPrefix}-link-${i}`}
            href={match[2]} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-700 underline font-medium hover:text-blue-900"
          >
            {match[1]}
          </a>
        );
      }
      return part;
    });
  };

  return (
    <div className="text-[15px] leading-relaxed">
      {lines.map((line, i) => (
        <p key={i} className={`min-h-[1em] ${i < lines.length - 1 ? 'mb-2' : ''}`}>
          {/* 2. Split by **bold** */}
          {line.split(/(\*\*.*?\*\*)/g).map((chunk, j) => {
            if (chunk.startsWith('**') && chunk.endsWith('**')) {
              return <strong key={j}>{chunk.slice(2, -2)}</strong>;
            }
            return <span key={j}>{parseLinks(chunk, j)}</span>;
          })}
        </p>
      ))}
    </div>
  );
}

// --- MAIN MESSAGE BUBBLE ---
function MessageBubble({ m }) {
  const isUser = m.role === "user";
  
  // 1. Safe Text Extraction
  const text = m.text || m.content || "";

  // 2. CHECK FOR IMAGE (Before formatting)
  const isImage = text.toString().startsWith("[IMAGE]");
  
  if (isImage) {
    const imageUrl = text.toString().replace("[IMAGE]", "").trim();
    return (
      <div className="self-start mt-2 mb-2 w-[280px] md:w-[320px] rounded-xl border border-black/10 overflow-hidden shadow-sm bg-white animate-popIn">
        <div className="w-full h-[180px] flex items-center justify-center p-4">
           <img 
             src={imageUrl} 
             alt="Product" 
             className="max-w-full max-h-full object-contain mix-blend-multiply"
             onError={(e) => e.target.style.display = 'none'} 
           />
        </div>
      </div>
    );
  }

  // 3. STANDARD TEXT (With Formatting & Cards)
  const hasResults = m.meta && m.meta.results && m.meta.results.length > 0;
  
  const containerClass = isUser 
    ? "self-end bg-[#6FA58E] text-[#1F2A24] rounded-br-sm" 
    : "self-start bg-[#FBF5DE] text-[#020612] rounded-bl-sm shadow-sm border border-black/5"; 

  return (
    <div className="flex flex-col gap-2 max-w-[95%] md:max-w-[85%] animate-popIn">
      <div 
        className={`
          relative px-5 py-3.5 rounded-2xl break-words
          ${containerClass} ${isUser ? "ml-auto" : "mr-auto"}
        `}
      >
        {/* USE OUR CUSTOM FORMATTER INSTEAD OF REACT-MARKDOWN */}
        <FormattedText text={text.toString()} />
      </div>

      {/* Product Cards */}
      {!isUser && hasResults && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-1 ml-1">
          {m.meta.results.map((laptop, idx) => (
            <ProductCard key={idx} product={laptop} />
          ))}
        </div>
      )}
    </div>
  );
}

// --- MAIN CHAT COMPONENT ---
export default function Chat({ conv, onSend }) {
  const [input, setInput] = useState("");
  const messagesRef = useRef(null);

  useEffect(() => {
    if (messagesRef.current) {
        messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [conv?.messages, conv?.loading]);

  function handleSend() {
    const text = input.trim();
    if (!text) return;
    onSend?.(text); 
    setInput("");
  }

  return (
    <div className="flex flex-col h-full w-full relative">
      <div ref={messagesRef} className="flex-1 overflow-y-auto w-full scroll-smooth">
        <div className="max-w-[900px] mx-auto px-4 py-6 flex flex-col gap-6">
          {!conv?.messages?.length && (
            <div className="mt-20 text-center text-[#6F7565] animate-fadeIn">
               <p className="text-lg font-medium">Hello! 👋</p>
               <p className="text-sm opacity-75">I can help you find laptops, phones, or components.</p>
            </div>
          )}
          
          {conv?.messages?.map((m, i) => (
             <MessageBubble key={i} m={m} />
          ))}

          {conv?.loading && (
             <div className="self-start flex gap-2 ml-2 items-center text-[#6F7565] text-sm animate-pulse">
                <div className="w-2 h-2 bg-stone-400 rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-stone-400 rounded-full animate-bounce delay-75"></div>
                <div className="w-2 h-2 bg-stone-400 rounded-full animate-bounce delay-150"></div>
                <span>Analyzing specs...</span>
             </div>
          )}
        </div>
      </div>

      <div className="shrink-0 w-full z-10 bg-[#f7eccc] border-t border-stone-200/50">
        <div className="max-w-[900px] mx-auto w-full">
           <ChatComposer 
              value={input} 
              onChange={setInput} 
              onSend={handleSend}
              loading={conv?.loading} 
            />
        </div>
      </div>
    </div>
  );
}